import React from 'react';
import { motion } from 'framer-motion';
import { BadgeCheckIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { sectors, sectorsMenuCopy, type Bi, type Sector } from '../../data/sectors';
import { sectorsEcosystems as c } from '../../data/sectorsPage';
import { SECTOR_ICONS } from './sectorIcons';
type Pick = (b: Bi) => string;
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};

/** Restrained visual cue: one person at the centre, connected to the ecosystem's relationships. */
function RelationCue({
  s,
  pick,
  large
}: {
  s: Sector;
  pick: Pick;
  large?: boolean;
}) {
  const Icon = SECTOR_ICONS[s.key];
  return <div className="flex items-center gap-3" data-id="element-6593" data-mp="9fetlgoekk8v">
      <span className={`rounded-full flex items-center justify-center shrink-0 ${large ? 'w-14 h-14 bg-[#4F46E5]' : 'w-11 h-11 bg-[#F1F0FD]'}`} data-id="element-6594" data-mp="9e3nvbbed2f4">
        <Icon className={`${large ? 'w-6 h-6 text-white' : 'w-5 h-5 text-[#4F46E5]'}`} strokeWidth={1.6} aria-hidden="true" data-id="element-6595" data-mp="1ynlhfn9xv9rz" />
      </span>
      <span className="w-6 h-px bg-[#D9D7F4] shrink-0" aria-hidden="true" data-id="element-6596" data-mp="28dyak3vtkb9q" />
      <ul className="flex flex-wrap gap-1.5 min-w-0" data-id="element-6597" data-mp="28g0xug5l7qar">
        {s.relations.map(r => <li key={r.fr} className="rounded-full border border-[#E7E6F3] bg-white px-2.5 py-1 font-sans text-[11.5px] font-medium text-[#3A4260] whitespace-nowrap" data-id="element-6598" data-mp="2deuc4asp7lbz">
            {pick(r)}
          </li>)}
      </ul>
    </div>;
}
function PrimaryCard({
  s,
  pick
}: {
  s: Sector;
  pick: Pick;
}) {
  return <article id={s.anchor} className="h-full scroll-mt-36 rounded-3xl border border-[#E2E0FA] bg-[#F7F6FE] p-7 lg:p-9 flex flex-col" data-id="element-6599" data-mp="1umjqruu3861m">
      <span className="self-start inline-flex items-center gap-1.5 rounded-full bg-white border border-[#DCDAF7] px-3 py-1 font-sans text-[11px] font-semibold tracking-[0.14em] uppercase text-[#4F46E5]" data-id="element-6600" data-mp="311u1st2mdaz4">
        <BadgeCheckIcon className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" data-id="element-6601" data-mp="15xckw2s2f0ly" />
        {pick(sectorsMenuCopy.badge)}
      </span>
      <h3 className="mt-6 font-serif text-[1.9rem] lg:text-[2.1rem] leading-tight text-[#121A3A]" data-id="element-6602" data-mp="1ezvoip3odh99">{pick(s.name)}</h3>
      <p className="mt-3 max-w-[420px] font-sans text-[15px] text-[#4A5268] leading-relaxed" data-id="element-6603" data-mp="1axqwfusvksoq">{pick(s.copy)}</p>
      <div className="mt-7" data-id="element-6604" data-mp="n7wfb11iwhyo">
        <RelationCue s={s} pick={pick} large data-id="element-6605" data-mp="n97ojgdqmhx6" />
      </div>
      <div className="mt-auto pt-8" data-id="element-6606" data-mp="ia4okacm71w2">
        <dl className="pt-6 border-t border-[#E2E0FA] grid grid-cols-3 gap-4" data-id="element-6607" data-mp="ib64ot8hmekn">
          {c.proof.map(p => <div key={p.label.fr} className="min-w-0" data-id="element-6608" data-mp="w3458fo4v257">
              <dt className="font-serif text-[1.35rem] lg:text-[1.5rem] leading-none text-[#4F46E5] whitespace-nowrap" data-id="element-6609" data-mp="ra1cg9r6jwq2">
                {typeof p.value === 'string' ? p.value : pick(p.value)}
              </dt>
              <dd className="mt-1.5 font-sans text-[12.5px] text-[#4A5268]" data-id="element-6610" data-mp="3vnpyxor5i9jy">{pick(p.label)}</dd>
            </div>)}
        </dl>
        <p className="mt-4 font-sans text-[12px] text-[#7A8098]" data-id="element-6611" data-mp="3lihrdvn73ev5">{pick(c.proofCaption)}</p>
      </div>
    </article>;
}
function SectorCard({
  s,
  pick
}: {
  s: Sector;
  pick: Pick;
}) {
  return <article id={s.anchor} className="h-full scroll-mt-36 rounded-2xl border border-[#ECEBF5] bg-white p-6 flex flex-col shadow-[0_14px_34px_-30px_rgba(18,26,58,0.35)]" data-id="element-6612" data-mp="3bkab7f0qd0u7">
      <RelationCue s={s} pick={pick} data-id="element-6613" data-mp="86zf7ojyt6n2" />
      <h3 className="mt-5 font-sans text-[16px] font-semibold text-[#121A3A] leading-snug" data-id="element-6614" data-mp="83tsgfyt6gui">{pick(s.name)}</h3>
      <p className="mt-2 font-sans text-[14px] text-[#4A5268] leading-relaxed" data-id="element-6615" data-mp="3ucwa686xp7j0">{pick(s.copy)}</p>
    </article>;
}
export function SectorsEcosystems() {
  const {
    lang
  } = useLanguage();
  const pick: Pick = b => lang === 'FR' ? b.fr : b.en;
  const [primary, ...others] = sectors;
  return <section className="relative bg-[#FCFCFE] border-t border-[#EEF0F4] px-6 py-20 lg:py-24" aria-labelledby="sectors-eco-title" data-id="element-6616" data-mp="2nwsq22bz1107">
      <div className="max-w-[1280px] mx-auto" data-id="element-6617" data-mp="344725530p6mi">
        <motion.div {...reveal} data-id="element-6618" data-mp="3461lwg2jysp0">
          <p className="font-sans text-[13px] font-medium tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-6619" data-mp="2zcoyg99m0rxu">{pick(c.eyebrow)}</p>
          <h2 id="sectors-eco-title" className="font-serif text-[2rem] sm:text-[2.5rem] lg:text-[2.8rem] leading-[1.06] tracking-tight mt-5" data-id="element-6620" data-mp="2uf6wj2u0fczc">
            {c.headline.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-6621" data-mp="3n3cgvoh5izyn">{pick(l.text)}</span>)}
          </h2>
        </motion.div>

        <motion.div {...reveal} className="mt-12 lg:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-5" data-id="element-6622" data-mp="3d4my7thflbhc">
          <div className="lg:col-span-5" data-id="element-6623" data-mp="385k597eq6vpq">
            <PrimaryCard s={primary} pick={pick} data-id="element-6624" data-mp="384qvdxkgzq3d" />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-6625" data-mp="21p8736bwldu5">
            {others.map(s => <SectorCard key={s.key} s={s} pick={pick} data-id="element-6626" data-mp="1wq3us6tomzzl" />)}
          </div>
        </motion.div>
      </div>
    </section>;
}